import { toast } from '../Components/Toast';
import { getMetaCampaigns, updateMetaObject } from '../services/metaAdsService';
import { getUserLotUnits } from '../services/userLotsService';
import { unshareUserLot } from '../services/userLotsShareService';
import { updateCadProject } from '../services/cadService';
import { readMetaCampaigns } from './metaCampaigns';
import { newMetaIdempotencyKey, readMetaWriteDryRun } from './metaWrite';
import { findMetaAutoShare, forgetMetaAutoShare } from './metaCreateShare';

/*
 * Campañas de Terrio que llevan a un link de compartir (decisión de Ignacio, t1098u-t1099u).
 * El anuncio creado desde Terrio lleva al link público del registro: la unidad, al share de SU LOTE
 * (`/#/shared-user-lot/{token}`, uno por lote); la parcela, al showroom (`/#/lista/{project_id}?parcel=`).
 *
 * Cómo se sabe qué campañas usan el link (no hay un dato directo en la API, ver la pregunta para APIBOT):
 * campañas de `getmetacampaigns` (todas, también en pausa) con la marca « [TR…]» en el nombre
 * (`createdInTerrio`) y con algún anuncio vinculado a un registro de ese link: cualquier unidad del lote,
 * o una parcela de ese showroom (`project_id` del vínculo). Las vinculadas a mano no cuentan: su
 * anuncio lleva adonde lo armó el dueño en Meta.
 *
 * Paso 2: antes de cortar el link (dejar de compartir, pasar a privado o sacar unidades del share del
 * lote) se avisa cuántas son y se ofrece pausar las activas con el pause de F2a (preview + confirm).
 * Paso 3: al eliminar una campaña de Terrio, si el share lo creó «Crear campaña» (`metaCreateShare`) y
 * ya no lo usa ninguna otra, se ofrece dejar de compartir.
 */

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const sameId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/**
 * Campañas de Terrio que llevan al link de `scope`.
 * @param {object[]} campaigns normalizadas (`normalizeMetaCampaign`).
 * @param {{ userLotIds?: string[], projectId?: string|null, parcelIds?: string[] }} scope
 * @param {{ excludeIds?: string[] }} [options]
 * @returns {Array<{ id: string, name: string, active: boolean, canPause: boolean }>}
 */
export function metaCampaignsUsingShare(campaigns, { userLotIds = [], projectId = null, parcelIds = [] } = {}, { excludeIds = [] } = {}) {
  const units = (userLotIds || []).map((x) => String(x).toLowerCase());
  const parcels = (parcelIds || []).map((x) => String(x).toLowerCase());
  const matches = (rec) => {
    if (rec.itemKind === 'user_lot') return units.includes(String(rec.id).toLowerCase());
    if (rec.itemKind === 'cad_parcel') {
      return parcels.includes(String(rec.id).toLowerCase()) || Boolean(projectId && rec.projectId && sameId(rec.projectId, projectId));
    }
    return false;
  };
  return (Array.isArray(campaigns) ? campaigns : [])
    .filter((c) => c?.createdInTerrio && !excludeIds.some((x) => sameId(x, c.id)))
    .filter((c) => (c.ads || []).some((ad) => (ad.linkedRecords || []).some(matches)))
    .map((c) => {
      const canPause = c.write?.canPause === true;
      return { id: c.id, name: c.name, active: canPause || c.isActiveInMeta === true, canPause };
    });
}

const plural = (n, one, many) => (n === 1 ? one : many);

/**
 * Textos de la confirmación (paso 2). `mode`: `unshare` (dejar de compartir), `restrict` (pasar a
 * privado) o `remove_units` (sacar unidades del link del lote).
 */
export function metaShareCutTexts(campaigns, mode = 'unshare') {
  const n = campaigns.length;
  const active = campaigns.filter((c) => c.active).length;
  const pausable = campaigns.filter((c) => c.canPause).length;
  const what = `${n} ${plural(n, 'campaña creada', 'campañas creadas')} desde Terrio que ${plural(n, 'lleva', 'llevan')} a este link`;
  let effect = 'Si lo cortás, quien toque el anuncio va a ver que no está disponible.';
  if (mode === 'restrict') effect = 'Si lo pasás a privado, quien toque el anuncio va a tener que iniciar sesión.';
  if (mode === 'remove_units') effect = 'Si sacás la unidad del link, quien toque el anuncio no la va a ver.';
  let activeText = '';
  if (active > 0) activeText = active === n ? (n === 1 ? ' Está activa.' : ' Están activas.') : ` ${active} ${plural(active, 'está activa', 'están activas')}.`;
  return {
    title: `Tenés ${n} ${plural(n, 'campaña', 'campañas')} con este link`,
    text: `Tenés ${what}. ${effect}${activeText}`,
    pauseLabel: pausable > 0 ? `Pausar ${plural(pausable, 'y seguir', `${pausable} y seguir`)}` : null,
    continueLabel: 'Seguir igual',
    pausable,
  };
}

export const META_SHARE_PAUSE_ERROR = 'No pudimos pausar todas. Revisalas en Campañas o seguí igual.';

/** Campañas de Terrio del link (`getmetacampaigns` con las inactivas). `null` si no se pudo leer. */
export async function loadMetaCampaignsUsingShare(scope, { excludeIds = [], signal } = {}) {
  const res = await getMetaCampaigns({ includeInactive: true, signal });
  if (!res?._httpOk) return null;
  return metaCampaignsUsingShare(readMetaCampaigns(res, { includeInactive: true }).campaigns, scope, { excludeIds });
}

/**
 * Pausa con el pause de F2a (preview `dry_run` → confirm con `expected` + key nueva), de a una.
 * @returns {Promise<{ paused: string[], failed: string[] }>}
 */
export async function pauseMetaCampaigns(ids) {
  const paused = [];
  const failed = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const dryRes = await updateMetaObject({ level: 'campaign', id, action: 'pause', dryRun: true });
    if (!dryRes?._httpOk) { failed.push(id); continue; } // eslint-disable-line no-continue
    const dry = readMetaWriteDryRun(dryRes);
    if (dry.noChange) { paused.push(id); continue; } // eslint-disable-line no-continue
    // eslint-disable-next-line no-await-in-loop
    const res = await updateMetaObject({
      level: 'campaign', id, action: 'pause', expected: dry.expected, idempotencyKey: newMetaIdempotencyKey(),
    });
    if (res?._httpOk) paused.push(id);
    else failed.push(id);
  }
  return { paused, failed };
}

// ── Paso 3: después de eliminar ─────────────────────────────────────────────

export const META_AUTO_UNSHARE_ACTION = 'Dejar de compartir';

/**
 * Registro (de los vínculos de la campaña eliminada) cuyo share creó «Crear campaña», o `null`.
 * Sincrónico: si no hay nada anotado, no se pide nada a la API.
 */
export function pickMetaAutoShareRecord(records) {
  return (Array.isArray(records) ? records : [])
    .map((rec) => ({ rec, entry: findMetaAutoShare({ itemKind: rec?.itemKind, id: rec?.id, projectId: rec?.projectId }) }))
    .find((x) => x.entry) || null;
}

/**
 * ¿Ofrecer dejar de compartir después de eliminar `deletedCampaignId`? Solo si el share lo creó «Crear
 * campaña» y ninguna otra campaña de Terrio lleva a ese link.
 * @returns {Promise<{ itemKind: string, scope: 'lot'|'project', id: string, message: string }|null>}
 */
export async function findMetaAutoUnshareOffer({ records, deletedCampaignId }) {
  const picked = pickMetaAutoShareRecord(records);
  if (!picked) return null;
  const { rec, entry } = picked;
  let scope;
  if (entry.scope === 'lot') {
    const unitsRes = await getUserLotUnits(entry.id);
    const ids = Array.isArray(unitsRes?.data) ? unitsRes.data.map((u) => str(String(u?.id || u?.user_lot_id || ''))).filter(Boolean) : [];
    scope = { userLotIds: ids.length ? ids : entry.recordIds };
  } else {
    scope = { projectId: entry.id };
  }
  const others = await loadMetaCampaignsUsingShare(scope, { excludeIds: [deletedCampaignId] });
  if (others == null || others.length > 0) return null;
  const showroom = entry.scope === 'project';
  return {
    itemKind: rec.itemKind,
    scope: entry.scope,
    id: entry.id,
    message: showroom
      ? 'El showroom se compartió para el anuncio y ya no lo usa ninguna campaña. ¿Dejar de compartirlo también?'
      : 'La unidad se compartió para el anuncio y ya no la usa ninguna campaña. ¿Dejar de compartirla también?',
  };
}

/** «Dejar de compartir» del toast: apaga el share (el de F2a no lo toca) y borra la marca. */
export async function runMetaAutoUnshare(offer) {
  if (!offer?.id) return { ok: false };
  try {
    if (offer.scope === 'project') {
      const res = await updateCadProject({ id: offer.id, is_shared: false });
      if (!res?.ok) return { ok: false, message: str(res?.message) };
    } else {
      const res = await unshareUserLot({ lot_id: offer.id });
      // 404: ya no estaba compartido → mismo estado final.
      if (!res?._httpOk && res?._httpStatus !== 404) return { ok: false, message: str(res?.message || res?.error) };
    }
  } catch {
    return { ok: false };
  }
  forgetMetaAutoShare({ scope: offer.scope, id: offer.id });
  return { ok: true, message: offer.scope === 'project' ? 'Dejaste de compartir el showroom.' : 'Dejaste de compartir la unidad.' };
}

/**
 * Después de eliminar una campaña de Terrio: si corresponde, el toast de éxito suma «¿Dejar de compartir…?»
 * con la acción (un solo aviso: reemplaza al toast de eliminar). Si no, no hace nada.
 * @param {{ records: object[], deletedCampaignId: string, baseMessage: string, onUnshared?: () => void }} input
 */
export async function offerMetaAutoUnshare({ records, deletedCampaignId, baseMessage = '', onUnshared = null }) {
  if (!pickMetaAutoShareRecord(records)) return null;
  let offer = null;
  try {
    offer = await findMetaAutoUnshareOffer({ records, deletedCampaignId });
  } catch {
    offer = null;
  }
  if (!offer) return null;
  toast.success({
    message: [baseMessage, offer.message].filter(Boolean).join(' '),
    actionLabel: META_AUTO_UNSHARE_ACTION,
    duration: 12000,
    onAction: async () => {
      const out = await runMetaAutoUnshare(offer);
      if (out.ok) {
        toast.success({ message: out.message });
        onUnshared?.();
      } else {
        toast.error({ title: 'No pudimos dejar de compartir', message: out.message || 'Probá de nuevo desde Compartir.' });
      }
    },
  });
  return offer;
}
